import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../../store/store";
import { STATUS } from "../../../../store/slices/OrganizationSlice";
import { fetchSupportDebugCashflowAsync } from "../../../../store/thunks/SupportDebugThunk";
import { CashflowShortfallResponse } from "../../../../services/SupportDebugService";
import { Badge } from "../../../Catalyst/badge";
import { Button } from "../../../Catalyst/button";
import { Input } from "../../../Catalyst/input";
import { Field, Label } from "../../../Catalyst/fieldset";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../Catalyst/table";
import BillsTable from "../BillsTable";
import { PanelErrorCard, PanelLoadingCard } from "../PanelStates";
import {
  categorySkipNote,
  eligibilitySkipReasonLabel,
  formatCurrency,
  formatDate,
  formatPercent,
  formatRatio,
  goalResolutionLabel,
  moneyHealthStatusDisplay,
  moneyHealthZoneColor,
  spendingHealthStateDisplay,
} from "../utils";

interface CashflowShortfallPanelProps {
  userId: string;
}

const LAYER_LABELS: Record<string, string> = {
  committed: "Committed (bills)",
  flexible: "Flexible (day-to-day spending)",
  goal: "Goal",
  clear: "Clear",
};

// Everything below renders verbatim from the API response — the widget block is
// what the app derives, the moneyHealth block is the engine's assessment, the
// algorithm block is the raw cashflow output. Nothing is recomputed here.
const CashflowResult = ({ data }: { data: CashflowShortfallResponse }) => {
  const { widget, moneyHealth, algorithm } = data;

  if (!widget.eligible) {
    return (
      <div className="mt-6 w-full rounded-lg border-2 border-amber-200 bg-amber-50 pt-5">
        <div className="flex items-start gap-3 px-6 pb-5">
          <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              This user doesn&rsquo;t qualify for the Spending Health widget — the app shows Safe
              to Spend instead
            </p>
            <p className="mt-1 text-sm text-amber-800">
              Reason: {eligibilitySkipReasonLabel(widget.skipReason)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stateDisplay = spendingHealthStateDisplay(widget.state);
  const statusDisplay = moneyHealthStatusDisplay(widget.moneyHealthStatus);

  return (
    <>
      {/* (a) What the user sees */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="px-6 pb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="desktext11 text-texthead">What the user sees</div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={statusDisplay.color}>{statusDisplay.label}</Badge>
              <span className="text-xs text-textColor3">&rarr;</span>
              <Badge color={stateDisplay.color}>{stateDisplay.label}</Badge>
            </div>
          </div>
          {/* heroNumber contains a true minus sign (U+2212) — render verbatim */}
          <div className="mt-4 text-4xl font-semibold tabular-nums text-texthead">
            {widget.heroNumber}
          </div>
          <div className="mt-2 text-base font-medium text-texthead">{widget.header}</div>
          <p className="mt-1 text-sm text-textColor">{widget.subtext}</p>
          <p className="mt-3 text-sm text-textColor3">
            {widget.goalName != null ? (
              <>
                Goal: <span className="font-medium text-texthead">{widget.goalName}</span> — target
                this period {formatCurrency(widget.periodGoalTarget)} &middot; margin{" "}
                <span className="tabular-nums">{formatCurrency(widget.moneyHealthMargin)}</span>
              </>
            ) : (
              <>No active goal — margin equals the cashflow figure</>
            )}
          </p>
          <dl className="mt-4 space-y-3 border-t border-shadowborder pt-4 text-sm">
            <div>
              <dt className="font-medium text-textColor3">AI chat prompt</dt>
              <dd className="mt-0.5 text-textColor">{widget.aiChatPrompt}</dd>
            </div>
            <div>
              <dt className="font-medium text-textColor3">Info tooltip</dt>
              <dd className="mt-0.5 text-textColor">{widget.infoTooltip}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* (b) Money health — the waterfall that decided the state */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="px-6 pb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="desktext11 text-texthead">Money health — why this state</div>
            {moneyHealth && (
              <div className="flex flex-wrap items-center gap-2 text-sm text-textColor3">
                <span>layer</span>
                <Badge color={moneyHealth.layer === "clear" ? "green" : "zinc"}>
                  {moneyHealth.layer}
                </Badge>
                <span>zone</span>
                <Badge color={moneyHealthZoneColor(moneyHealth.zone)}>{moneyHealth.zone}</Badge>
              </div>
            )}
          </div>
          {moneyHealth ? (
            <>
              <p className="mt-1 text-sm text-textColor3">
                Money flows committed &rarr; flexible &rarr; goal; a layer decides the status only
                when its position is negative, judged against that layer&rsquo;s size. A narrowly
                covered layer is kept as context and evaluation continues. Bands per layer:{" "}
                {(Object.keys(moneyHealth.thresholds) as Array<keyof typeof moneyHealth.thresholds>).map(
                  (layer, index) => (
                    <span key={layer}>
                      {index > 0 && " · "}
                      {layer} deep &gt; {formatPercent(moneyHealth.thresholds[layer].deep)} short,
                      marginal &le; {formatPercent(moneyHealth.thresholds[layer].marginal)} past
                    </span>
                  )
                )}
                .
              </p>
              <div className="mt-4">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Layer</TableHeader>
                      <TableHeader className="text-right">Position</TableHeader>
                      <TableHeader className="text-right">Size</TableHeader>
                      <TableHeader className="text-right">Ratio</TableHeader>
                      <TableHeader>Zone</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {moneyHealth.layers.map((row) => (
                      <TableRow key={row.layer}>
                        <TableCell className="font-medium text-texthead">
                          {LAYER_LABELS[row.layer] ?? row.layer}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(row.position)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(row.size)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatRatio(row.ratio)}</TableCell>
                        <TableCell>
                          <Badge color={moneyHealthZoneColor(row.zone)}>{row.zone}</Badge>
                          {row.zone === "marginal" && moneyHealth.layer !== row.layer && (
                            <span className="ml-2 text-xs text-textColor3">
                              narrowly covered &mdash; continued
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-shadowborder pt-4 text-sm sm:grid-cols-3">
                {[
                  ["Income", moneyHealth.income],
                  ["Committed", moneyHealth.committed],
                  ["Flexible spending", moneyHealth.flexible_spending],
                  ["Cashflow", moneyHealth.cashflow],
                  ["Period goal target", moneyHealth.period_goal_target],
                  ["Money health margin", moneyHealth.money_health_margin],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <dt className="font-medium text-textColor3">{label}</dt>
                    <dd className="mt-0.5 tabular-nums text-texthead">{formatCurrency(value as number)}</dd>
                  </div>
                ))}
                <div>
                  <dt className="font-medium text-textColor3">Goal</dt>
                  <dd className="mt-0.5 text-texthead">
                    {moneyHealth.goal_name ?? "—"}
                    <span className="block text-xs text-textColor3">
                      {goalResolutionLabel(moneyHealth.goal_resolution)}
                    </span>
                  </dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="mt-1 text-sm text-textColor3">
              No money-health assessment was returned for this run.
            </p>
          )}
          <div className="mt-5 text-sm font-medium text-texthead">Status ladder</div>
          <ul className="mt-2 space-y-2">
            {(widget.stateLadder ?? []).map((row) => {
              const rowDisplay = moneyHealthStatusDisplay(row.state);
              const rowWidgetDisplay = spendingHealthStateDisplay(row.widgetState);
              return (
                <li
                  key={row.state}
                  className={`flex items-start justify-between gap-4 rounded-lg border px-4 py-3 ${
                    row.matched
                      ? "border-green-500 bg-green-50 ring-1 ring-green-500"
                      : "border-shadowborder"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {row.matched && <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />}
                      <span className="text-sm font-medium text-texthead">{rowDisplay.label}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-textColor">{row.rule}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge color={rowDisplay.color}>{row.state}</Badge>
                    <span className="text-xs text-textColor3" title="Widget state this status maps to">
                      &rarr; {rowWidgetDisplay.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* (c) The math */}
      {data.formula && (
        <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
          <div className="px-6">
            <div className="desktext11 text-texthead">The math</div>
            <p className="mt-1 font-mono text-xs text-textColor3">{data.formula.expression}</p>
            {widget.payPeriod && (
              // daysElapsed is a 0-based completed-days counter (0 on payday),
              // so phrase it as elapsed/remaining rather than "day N of M".
              <p className="mt-2 text-sm text-textColor3">
                Pay period: {formatDate(widget.payPeriod.start)} &rarr;{" "}
                {formatDate(widget.payPeriod.end)} — {widget.payPeriod.daysElapsed} day(s) elapsed
                &middot; {widget.payPeriod.daysRemaining} remaining
              </p>
            )}
          </div>
          <div className="mt-4 divide-y divide-shadowborder border-t border-shadowborder">
            {data.formula.components.map((component) => (
              <div key={component.key} className="flex items-center justify-between gap-6 px-6 py-3">
                <span className="text-sm text-texthead">{component.label}</span>
                <span className="shrink-0 text-right text-sm tabular-nums text-texthead">
                  {formatCurrency(component.value)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-6 px-6 py-3">
              <span className="text-sm font-semibold text-texthead">Shortfall value</span>
              <span className="shrink-0 text-right text-base font-bold tabular-nums text-texthead">
                {formatCurrency(data.formula.result.value)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* (d) Per-category projection */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Per-category projection</div>
        {algorithm.category_detail.length === 0 ? (
          <div className="flex items-center justify-center px-6 py-10">
            <p className="text-sm font-medium text-texthead opacity-70">No category projections</p>
          </div>
        ) : (
          <div className="px-6 pb-4">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Kind</TableHeader>
                  <TableHeader className="text-right">Spent</TableHeader>
                  <TableHeader className="text-right">Burn rate</TableHeader>
                  <TableHeader className="text-right">Monthly limit</TableHeader>
                  <TableHeader className="text-right">Monthly bills</TableHeader>
                  <TableHeader className="text-right">Discretionary</TableHeader>
                  <TableHeader className="text-right">Period cap</TableHeader>
                  <TableHeader className="text-right">Projected</TableHeader>
                  <TableHeader>Note</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {algorithm.category_detail.map((row) => (
                  <TableRow key={row.catg_id}>
                    <TableCell className="font-medium text-texthead">
                      {row.category_name || `Category ${row.catg_id}`}
                    </TableCell>
                    <TableCell>
                      <Badge color={row.kind === "elastic" ? "blue" : "zinc"}>{row.kind}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.spent)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.burn_rate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.monthly_limit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.monthly_recurring)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.discretionary_limit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.pay_period_cap)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={`Source: ${row.source}`}>
                      {formatCurrency(row.projected)}
                    </TableCell>
                    <TableCell className="text-textColor">{categorySkipNote(row.skip_reason)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* (e) Bills paid / bills remaining */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Bills already paid this period</div>
        <BillsTable
          bills={data.billsPaidList}
          emptyMessage="No bills have landed yet this pay period"
        />
      </div>
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Bills still due this period</div>
        <BillsTable
          bills={widget.billsRemainingList ?? []}
          // A positive remaining total with no itemized rows means the two
          // reads disagreed — say so instead of claiming there are no bills.
          emptyMessage={
            (widget.billsRemaining ?? 0) > 0
              ? `Total shows ${formatCurrency(widget.billsRemaining)} but no itemized bills were returned — data may have changed between reads; refresh to reconcile.`
              : "No bills remaining this pay period"
          }
        />
      </div>

      {/* (f) Daily spend */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Daily spend (last 7 days)</div>
        {(widget.dailySpendLast7Days ?? []).length === 0 ? (
          <div className="flex items-center justify-center px-6 py-10">
            <p className="text-sm font-medium text-texthead opacity-70">No daily spend data</p>
          </div>
        ) : (
          <div className="px-6">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Date</TableHeader>
                  <TableHeader className="text-right">Spent</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {(widget.dailySpendLast7Days ?? []).map((day) => (
                  <TableRow key={day.date}>
                    <TableCell>{formatDate(day.date)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(day.spent)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="px-6 pb-4 pt-2 text-xs text-textColor3">
          Rolling window ending today — not affected by the as-of date.
        </p>
      </div>
    </>
  );
};

const CashflowShortfallPanel = ({ userId }: CashflowShortfallPanelProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, status, error, requestedAsOf } = useSelector(
    (state: RootState) => state.SupportDebugStore.cashflow
  );
  // Empty means "today" (the endpoint's default when asOfDate is omitted).
  // Initialize from the asOfDate the current data was requested with so
  // re-entering the tab shows the input matching what's on screen.
  const [asOfDate, setAsOfDate] = useState(requestedAsOf ?? "");

  useEffect(() => {
    if (userId && status === STATUS.IDLE) {
      dispatch(fetchSupportDebugCashflowAsync({ userId }));
    }
  }, [userId, status, dispatch]);

  const handleAsOfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAsOfDate(value);
    dispatch(fetchSupportDebugCashflowAsync({ userId, asOfDate: value || undefined }));
  };

  const handleResetAsOf = () => {
    setAsOfDate("");
    dispatch(fetchSupportDebugCashflowAsync({ userId }));
  };

  return (
    <>
      <div className="mt-6 flex flex-wrap items-end gap-3">
        <Field className="w-56">
          <Label>As of date</Label>
          <Input type="date" value={asOfDate} onChange={handleAsOfChange} aria-label="As of date" />
        </Field>
        {asOfDate && (
          <Button outline onClick={handleResetAsOf}>
            Reset to today
          </Button>
        )}
        {data && status === STATUS.SUCCESS && (
          <p className="pb-2 text-sm text-textColor3">Computed as of {formatDate(data.asOf)}</p>
        )}
      </div>

      {(status === STATUS.LOADING || status === STATUS.IDLE) && <PanelLoadingCard />}
      {status === STATUS.FAILED && <PanelErrorCard error={error} />}
      {status === STATUS.SUCCESS && data && <CashflowResult data={data} />}
    </>
  );
};

export default CashflowShortfallPanel;
